// src/components/users/UserAiPanel.js
// --- UPDATE (User Management) ------------------------------------------------------------
// FlowSense AI for the team: health score, ranked findings from GET /users/insights (role coverage,
// span of control, workload outliers by z-score, temporary passwords, missing phone numbers,
// inactive users holding work…), each with an action (filter the table / add the missing role),
// plus "Ask FlowSense AI" (Ollama, module user_management) with the built-in rules as fallback.
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Flex, HStack, VStack, Text, Badge, Button, Icon, Input, IconButton, Spinner, Wrap, WrapItem, CircularProgress, CircularProgressLabel,
    Tooltip, Skeleton, useColorModeValue,
} from '@chakra-ui/react';
import { MdAutoAwesome, MdSend, MdArrowForward, MdRefresh } from 'react-icons/md';
import { askFlowSenseAi } from '../../services/flowsenseAiService';
import { answerUsersLocally, buildUsersAiContext, USER_QUESTIONS } from './userAiIntel';

const LEVEL = { critical: 'red', high: 'orange', medium: 'yellow', info: 'blue' };

const UserAiPanel = ({ insights, isLoading, onRefresh, onAction, users, catalog, canCreate = true }) => {
    const [q, setQ] = useState('');
    const [history, setHistory] = useState([]);
    const [asking, setAsking] = useState(false);
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    const bg = useColorModeValue('linear(135deg, purple.50, white 60%, blue.50)', 'linear(135deg, rgba(128,90,213,0.16), gray.800 60%, rgba(49,130,206,0.12))');
    const border = useColorModeValue('purple.100', 'whiteAlpha.200');
    const tile = useColorModeValue('white', 'gray.800');
    const muted = useColorModeValue('gray.500', 'gray.400');

    const ask = useCallback(async (text) => {
        const question = String(text || '').trim();
        if (!question || asking) return;
        setQ(''); setAsking(true);
        let entry;
        try {
            const r = await askFlowSenseAi(question, buildUsersAiContext({ users, insights, catalog }), 'user_management');
            if (!r?.answer) throw new Error('Empty answer');
            entry = { q: question, answer: r.answer, source: 'ollama', model: r.model };
        } catch (e) {
            entry = { q: question, ...answerUsersLocally(question, { users, insights }), note: e?.message };
        }
        if (mounted.current) { setHistory((h) => [{ ...entry, at: Date.now() }, ...h].slice(0, 6)); setAsking(false); }
    }, [asking, users, insights, catalog]);

    const score = insights?.score;
    const color = score == null ? 'gray' : score >= 85 ? 'green' : score >= 65 ? 'yellow' : 'red';

    return (
        <Box bgGradient={bg} borderWidth="1px" borderColor={border} borderRadius="2xl" p={4} role="region" aria-label="FlowSense AI team insights">
            <Flex align="center" gap={3} mb={3} wrap="wrap">
                <CircularProgress value={score ?? 0} color={`${color}.400`} size="56px" thickness="10px" isIndeterminate={isLoading && !insights}>
                    <CircularProgressLabel fontSize="sm" fontWeight="900">{score ?? '—'}</CircularProgressLabel>
                </CircularProgress>
                <Box flex={1} minW="200px">
                    <HStack spacing={2}><Icon as={MdAutoAwesome} color="purple.400" /><Text fontWeight="900">FlowSense AI · team health</Text>{insights && <Badge colorScheme={color}>{insights.grade}</Badge>}</HStack>
                    <Text fontSize="xs" color={muted}>
                        {insights ? `${insights.totals.active} active of ${insights.totals.users} · ${insights.totals.office} office · ${insights.totals.field} field${insights.span_of_control != null ? ` · 1 supervisor per ${insights.span_of_control} field users` : ''}${insights.workload?.mean != null ? ` · avg ${insights.workload.mean} open jobs per field user` : ''}` : 'Analysing roles, coverage, workload and account security…'}
                    </Text>
                </Box>
                <IconButton size="sm" variant="ghost" icon={<Icon as={MdRefresh} />} aria-label="Refresh insights" onClick={onRefresh} isLoading={isLoading} />
            </Flex>

            {isLoading && !insights ? <Skeleton h="60px" borderRadius="lg" /> : (insights?.findings || []).length === 0 ? (
                <Text fontSize="sm" color={muted} mb={3}>Nothing needs attention — roles, supervision and accounts look healthy.</Text>
            ) : (
                <VStack align="stretch" spacing={2} mb={3}>
                    {insights.findings.slice(0, 6).map((f) => (
                        <Flex key={f.id} bg={tile} p={2.5} borderRadius="lg" borderLeftWidth="4px" borderLeftColor={`${LEVEL[f.level] || 'gray'}.400`} gap={3} align="center">
                            <Box flex={1} minW={0}>
                                <HStack spacing={2}><Badge colorScheme={LEVEL[f.level]} fontSize="2xs">{f.level}</Badge><Text fontSize="sm" fontWeight="700">{f.title}</Text>{f.ml && <Tooltip hasArrow label={`Statistical outlier detection: ${f.ml}`}><Badge colorScheme="purple" fontSize="2xs" cursor="help">ML</Badge></Tooltip>}</HStack>
                                <Text fontSize="xs" color={muted} mt={0.5}>{f.detail}</Text>
                            </Box>
                            {f.action && onAction && (f.action.type !== 'create' || canCreate) && (
                                <Button size="xs" rightIcon={<Icon as={MdArrowForward} />} onClick={() => onAction(f.action)} flexShrink={0}>
                                    {f.action.type === 'create' ? 'Add user' : 'Show'}
                                </Button>
                            )}
                        </Flex>
                    ))}
                </VStack>
            )}

            <Wrap spacing={1} mb={2}>
                {USER_QUESTIONS.map((x) => <WrapItem key={x}><Button size="xs" variant="outline" colorScheme="purple" onClick={() => ask(x)} isDisabled={asking}>{x}</Button></WrapItem>)}
            </Wrap>
            <HStack as="form" onSubmit={(e) => { e.preventDefault(); ask(q); }}>
                <Input size="sm" bg={tile} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask FlowSense about your team, roles or permissions…" aria-label="Ask FlowSense AI about users" maxLength={500} />
                <IconButton type="submit" size="sm" colorScheme="purple" icon={asking ? <Spinner size="xs" /> : <Icon as={MdSend} />} isDisabled={asking || !q.trim()} aria-label="Ask" />
            </HStack>
            <VStack align="stretch" spacing={2} mt={3} aria-live="polite">
                {history.map((h) => (
                    <Box key={h.at} bg={tile} p={3} borderRadius="lg">
                        <HStack justify="space-between" mb={1}>
                            <Text fontSize="xs" fontWeight="700" noOfLines={1}>{h.q}</Text>
                            <Badge colorScheme={h.source === 'ollama' ? 'purple' : 'gray'} fontSize="2xs">{h.source === 'ollama' ? `FlowSense AI${h.model ? ` · ${h.model}` : ''}` : 'FlowSense rules'}</Badge>
                        </HStack>
                        <Text fontSize="sm" whiteSpace="pre-wrap">{h.answer}</Text>
                    </Box>
                ))}
            </VStack>
        </Box>
    );
};

UserAiPanel.propTypes = { insights: PropTypes.object, isLoading: PropTypes.bool, onRefresh: PropTypes.func, onAction: PropTypes.func, users: PropTypes.array, catalog: PropTypes.object, canCreate: PropTypes.bool };

export default UserAiPanel;
